import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';

import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';

import { varAlpha } from 'src/theme/styles';
import { AuthLayout } from 'src/layouts/auth';
import { DashboardLayout } from 'src/layouts/dashboard';
import {AdminLayout} from 'src/layouts/admin'
import { PrivateRoute } from './components/PrivateRoute';

// ----------------------------------------------------------------------

export const HomePage = lazy(() => import('src/pages/home'));
export const BlogPage = lazy(() => import('src/pages/blog'));
export const UserPage = lazy(() => import('src/pages/user'));
export const SignInPage = lazy(() => import('src/pages/sign-in'));
export const ForgotPasswordPage = lazy(() => import('src/pages/forgot-password'));
export const ProductsPage = lazy(() => import('src/pages/products'));
export const Page404 = lazy(() => import('src/pages/page-not-found'));
export const DashboardMini = lazy(() => import('src/pages/dashboard-mini'));
export const SignUpPage = lazy(() => import('src/pages/sign-up'));
export const ProfilePage = lazy(() => import('src/pages/profile'));
export const ChangePasswordPage = lazy(() => import('src/pages/change-password'));
export const CompanyInfoPage = lazy(() => import('src/pages/company-info'));
export const CompanyVcarbonPage = lazy(() => import('src/pages/company-vcarbon'));
export const CompanyVcarbonReportPage = lazy(() => import('src/pages/company-vcarbon-report'));
export const CompanyVcarbonReportUncertaintyPage = lazy(() => import('src/pages/company-vcarbon-report-uncertainty'));
export const CompanyExplainPage = lazy(() => import('src/pages/company-explain'));
export const CompanyReportPage = lazy(() => import('src/pages/company-report'));
export const UserManualPage = lazy(() => import('src/pages/user-manual'));
export const ContactPage = lazy(() => import('src/pages/contact'));
export const CompanyDocumentPage = lazy(() => import('src/pages/company-document'));

// admin
export const UserAdminPage = lazy(() => import('src/pages/admin/user-admin'));
export const VCarbonTypePage = lazy(() => import('src/pages/admin/vcarbon-type'));
export const VCarbonFormulaPage = lazy(() => import('src/pages/admin/vcarbon-formula'));
export const VCarbonPage= lazy(() => import('src/pages/admin/vcarbon'));
export const CompanyPage= lazy(() => import('src/pages/admin/company'));
export const VCarbonFormulaTestPage = lazy(() => import('src/pages/admin/vcarbon-formula-test'));
export const BusinessFieldTypePage = lazy(() => import('src/pages/admin/business-field-type'));
export const BusinessFieldDefinePage = lazy(() => import('src/pages/admin/business-field-define'));
export const BusinessFieldCompanyPage = lazy(() => import('src/pages/admin/business-field-company'));
export const CompanyUserPage = lazy(() => import('src/pages/admin/company-user'));

// ----------------------------------------------------------------------

const renderFallback = (
  <Box display="flex" alignItems="center" justifyContent="center" flex="1 1 auto">
    <LinearProgress
      sx={{
        width: 1,
        maxWidth: 320,
        bgcolor: (theme) => varAlpha(theme.vars.palette.text.primaryChannel, 0.16),
        [`& .${linearProgressClasses.bar}`]: { bgcolor: 'text.primary' },
      }}
    />
  </Box>
);

export function Router() {
  return useRoutes([
    {
      element: (
        <DashboardLayout>
          <Suspense fallback={renderFallback}>
              {/* <Outlet/> */}
              <PrivateRoute/>
          </Suspense>
        </DashboardLayout>
      ),
      children: [
        { element: <DashboardMini />, index: true },
        { path: 'user', element: <UserPage /> },
        { path: 'products', element: <ProductsPage /> },
        { path: 'blog', element: <BlogPage /> },
        { path: 'dashboard-mini', element: <DashboardMini /> },
        { path: 'profile', element: <ProfilePage />},
        { path: '/company-vcarbon/:companyId/:reportId', element: <CompanyVcarbonPage /> },
        { path: '/company-vcarbon-report/:companyId/:reportId', element: <CompanyVcarbonReportPage /> },
        { path: '/company-vcarbon-report-uncertainty/:companyId/:reportId', element: <CompanyVcarbonReportUncertaintyPage /> },
        { path: '/company-explain', element: <CompanyExplainPage /> },
        { path: '/company-report', element: <CompanyReportPage /> },
        { path: 'change-password', element: <ChangePasswordPage />},
        { path: 'company-info', element: <CompanyInfoPage />},
        { path: 'contact', element: <ContactPage />},
        { path: 'user-manual', element: <UserManualPage />},
        { path: '/company-document', element: <CompanyDocumentPage />},

        // { path: 'dashboard-mini', element: <DashboardMini /> },
      ],
    },
    {
      path: "admin",
      element: (
        <AdminLayout>
          <Suspense fallback={renderFallback}>
            <PrivateRoute/>
          </Suspense>
        </AdminLayout>
      ),
      children: [
        { element: <UserAdminPage />, index: true , },
        { path: '/admin/account', element: <UserAdminPage /> },
        { path: '/admin/permission', element: <ProductsPage /> },
        { path: '/admin/vcarbon-type', element: <VCarbonTypePage  /> },
        { path: '/admin/vcarbon-formula/:id', element: <VCarbonFormulaPage  /> },
        { path: '/admin/vcarbon', element: <VCarbonPage /> },
        { path: '/admin/company', element: <CompanyPage /> },
        { path: '/admin/company-user/:id', element: <CompanyUserPage /> },
        { path: '/admin/vcarbon-formula-test/:id', element: <VCarbonFormulaTestPage  /> },
        { path: '/admin/business-field-type', element: <BusinessFieldTypePage/>} ,
        { path: '/admin/business-field-define', element: <BusinessFieldDefinePage/>} ,
        { path: '/admin/business-field-company/:id', element: <BusinessFieldCompanyPage/>} ,

        { path: '/admin/profile', element: <ProfilePage />},
        { path: '/admin/change-password', element: <ChangePasswordPage />},
      ],
    },
    {
      path: 'sign-in',
      element: (
        <AuthLayout>
          <SignInPage />
        </AuthLayout>
      ),
    },
    {
      path: 'sign-up',
      element: (
        <AuthLayout>
          <SignUpPage />
        </AuthLayout>
      ),
    },
    {
      path: 'forgot-password',
      element: (
        <AuthLayout>
          <ForgotPasswordPage />
        </AuthLayout>
      ),
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}
